import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Highlighter from 'react-highlight-words';
import {
  Breadcrumb,
  Button,
  DatePicker,
  Flex,
  Input,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  HomeOutlined,
  QuestionOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import PropTypes from 'prop-types';
import SEO from './SEO';
import compareTwoValues from '../common/compareTwoValues';
import {
  colourPalette,
  dateFormat,
  filterTimeout,
  notificationTimeout,
  notificationType,
  query100,
  requestStatus,
  scrollHeight,
} from '../common/constants';
import pluralise from '../common/pluralise';
import validateString from '../common/validator';
import { db } from '../firebase';
import './style.scss';

export default function Onsite({ site }) {
  const [data, setData] = useState([]);
  const [filterDate, setFilterDate] = useState({ end: '', start: '' }); // date filter
  const [loading, setLoading] = useState(false);
  const [searchColumn, setSearchColumn] = useState('');
  const [searchText, setSearchText] = useState('');

  const [messageApi, contextHolder] = message.useMessage();

  // const fileDate = (date) =>
  //   date !== '' ? `_${date.split('-').join('')}` : '';
  // const filename = `vmsResident${fileDate(filterDate.start)}${fileDate(filterDate.end)}.csv`;
  const searchInput = useRef(null);
  const tableParams = {
    position: ['bottomCenter'],
    showSizeChanger: true,
    showTotal: (total, range) =>
      `${range[0]} - ${range[1]} of ${total} ${pluralise(total, 'record')}`,
    total: data.length,
  };

  // destructure imports
  const { RangePicker } = DatePicker;
  const { antdDefault, antdError, antdSuccess, highlighted, searchActive } =
    colourPalette;
  const { timeoutInfo, timeoutSuccess, timeoutWarning } = notificationTimeout;
  const { typeInfo, typeSuccess, typeWarning } = notificationType;
  const { approvedStr, pendingsStr, rejectedStr } = requestStatus;
  const { id } = site;

  const filterIconColour = (filtered) => (
    <SearchOutlined style={{ color: filtered ? searchActive : undefined }} />
  );

  const filterMenu = (
    clearFilters,
    close,
    confirm,
    dataIndex,
    handleReset,
    handleSearch,
    inputSearch, // searchInput
    selectedKeys,
    // setSearchColumn,
    // setSearchText,
    setSelectedKeys,
  ) => (
    <div className="search-dialog">
      <Input
        id="search-input"
        onChange={(e) =>
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={() =>
          handleSearch(true, confirm, dataIndex, selectedKeys)
        }
        placeholder={`Search ${dataIndex}`}
        ref={inputSearch}
        value={selectedKeys[0]}
      />
      <Space>
        <Button
          className="search-button"
          icon={<SearchOutlined />}
          onClick={() => handleSearch(true, confirm, dataIndex, selectedKeys)}
          size="small"
          type="primary"
        >
          Search
        </Button>
        <Button
          className="search-button"
          onClick={() => handleReset(clearFilters, confirm, dataIndex)}
          size="small"
        >
          Reset
        </Button>
        <Button
          onClick={() => handleSearch(false, confirm, dataIndex, selectedKeys)}
          size="small"
          type="link"
        >
          Filter
        </Button>
        <Button onClick={() => close()} size="small" type="link">
          Close
        </Button>
      </Space>
    </div>
  );

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const fetchRecords = useCallback(
    (dateFilter, siteId) => {
      if (validateString(siteId) === false) {
        console.warn('-- Site ID invalid');
        return;
      }

      setLoading(true);

      const records = [];
      const onsiteRef = collection(db, 'onsite');
      let q = query(onsiteRef, where('siteId', '==', siteId));

      // add date filters (if applicable)
      if (dateFilter.start !== '' && dateFilter.end !== '') {
        q = query(
          q,
          where('registrationDate', '>=', dateFilter.start),
          where('registrationDate', '<=', dateFilter.end),
        );
      }

      // order query results
      q = query(q, orderBy('registrationDate', 'desc'), limit(query100));

      getDocs(q)
        .then((querySnapshot) => {
          querySnapshot.forEach((doc) => {
            const record = {};
            const dict = doc.data();

            record.blockNumber = dict.blockNumber;
            record.companyName = dict.companyName ?? '--';
            record.contactNumber = dict.contactNumber;
            record.nricFinLast4 = dict.nricFinLast4;
            record.registerID = doc.id;
            record.registration = `${dict.registrationDate} ${dict.registrationTime}`;
            record.requestStatus = dict.requestStatus;
            record.residentName = dict.residentName ?? '--';
            record.unitNumber = dict.unitNumber;
            record.validDate = dict.validDate ?? '--';
            record.vehicleNumber = dict.vehicleNumber ?? '--';
            record.visitPurpose = dict.visitPurpose;
            record.visitType = dict.visitType;
            record.visitorName = dict.visitorName;

            records.push(record);
          });

          if (records?.length) {
            openMessage(
              `fetching latest ${records.length} ${pluralise(records.length, 'record')}`,
              timeoutSuccess,
              typeSuccess,
            );
          } else {
            console.warn('-- No record');
            openMessage('No record', timeoutInfo, typeInfo);
          }

          setData(records);
          setLoading(false);
        })
        .catch((error) => {
          setData([]);
          setLoading(false);
          console.warn('-- Error getting documents:', error);
          openMessage('Error getting documents', timeoutWarning, typeWarning);
        });
    },
    [
      openMessage,
      timeoutInfo,
      timeoutSuccess,
      timeoutWarning,
      typeInfo,
      typeSuccess,
      typeWarning,
    ],
  );

  const handleDatePickerChange = (dates) => {
    if (dates === null) {
      setFilterDate({ start: '', end: '' });
    } else {
      const date1 = dayjs(dates[0]).format(dateFormat);
      const date2 = dayjs(dates[1]).format(dateFormat);
      setFilterDate({ start: date1, end: date2 });
    }
  };

  const handleReload = () => {
    fetchRecords(filterDate, id);
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    clearFilters();
    confirm({ closeDropdown: false }); // dropdown stays open after searching
    setSearchColumn(dataIndex); // the column to search in
    setSearchText(''); // reset search terms
  };

  const handleSearch = (closeDropdown, confirm, dataIndex, selectedKeys) => {
    confirm({ closeDropdown }); // whether dropdown should close after searching
    setSearchColumn(dataIndex); // the column to search in
    setSearchText(selectedKeys[0]); // the search terms
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      clearFilters,
      close,
      confirm,
      selectedKeys,
      setSelectedKeys,
    }) =>
      filterMenu(
        clearFilters,
        close,
        confirm,
        dataIndex,
        handleReset,
        handleSearch,
        searchInput,
        selectedKeys,
        // setSearchColumn,
        // setSearchText,
        setSelectedKeys,
      ),
    filterIcon: (filtered) => filterIconColour(filtered),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), filterTimeout);
        }
      },
    },
    render: (text) =>
      searchColumn === dataIndex ? (
        <Highlighter
          autoEscape
          highlightStyle={{ backgroundColor: highlighted, padding: 0 }}
          searchWords={[searchText]}
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: 'Status',
      dataIndex: 'requestStatus',
      align: 'center',
      width: 140,
      render: (status) => {
        switch (status) {
          case 1:
            return (
              <Tag
                bordered={false}
                className="table icon-tag"
                color={antdSuccess}
                icon={<CheckOutlined />}
              >
                {approvedStr.toUpperCase()}
              </Tag>
            );
          case 2:
            return (
              <Tag
                bordered={false}
                className="table icon-tag"
                color={antdError}
                icon={<CloseOutlined />}
              >
                {rejectedStr.toUpperCase()}
              </Tag>
            );
          default:
            return (
              <Tag
                bordered={false}
                className="table icon-tag"
                color={antdDefault}
                icon={<QuestionOutlined />}
              >
                {pendingsStr.toUpperCase()}
              </Tag>
            );
        }
      },
    },
    {
      title: 'Registration',
      dataIndex: 'registration',
      width: 140,
      sortDirections: ['descend', 'ascend'],
      sorter: {
        compare: (a, b) => compareTwoValues(a.registration, b.registration),
        multiple: 1,
      },
    },
    {
      title: 'ID',
      dataIndex: 'registerID',
      width: 260,
      render: (registerID) => <code className="mono">{registerID}</code>,
    },
    {
      title: 'Block',
      dataIndex: 'blockNumber',
      width: 100,
      ...getColumnSearchProps('blockNumber'),
    },
    {
      title: 'Unit',
      dataIndex: 'unitNumber',
      width: 80,
      ...getColumnSearchProps('unitNumber'),
    },
    {
      title: 'Resident Name',
      dataIndex: 'residentName',
      width: 200,
      ...getColumnSearchProps('residentName'),
    },
    {
      title: 'Company Name',
      dataIndex: 'companyName',
      width: 160,
      ...getColumnSearchProps('companyName'),
    },
    {
      title: 'Visitor Name',
      dataIndex: 'visitorName',
      width: 200,
      ...getColumnSearchProps('visitorName'),
    },
    {
      title: 'Visitor Contact',
      dataIndex: 'contactNumber',
      width: 160,
    },
    {
      title: 'NRIC',
      dataIndex: 'nricFinLast4',
      width: 100,
      render: (lastFour) => `*****${lastFour}`,
    },
    {
      title: 'Valid On',
      dataIndex: 'validDate',
      width: 120,
    },
    // {
    //   vehicle number, visitpurpose, visittype
    // },
  ];

  useEffect(() => fetchRecords(filterDate, id), [fetchRecords, filterDate, id]);

  return (
    <>
      <SEO
        description="Review onsite visitors"
        name="qiwei"
        title="Onsite"
        type="webapp"
      />
      <div>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="..">
                  <HomeOutlined className="dropdown-icon-margin" />
                  <span>Home</span>
                </Link>
              ),
            },
            {
              title: <span>Onsite</span>,
            },
          ]}
        />
        <Flex align="center" wrap>
          <RangePicker
            format={dateFormat}
            id="rangePicker"
            name="rangePicker"
            onChange={handleDatePickerChange}
            showNow
            variant="borderless"
          />
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={handleReload}
            type="link"
          >
            Reload
          </Button>
        </Flex>
      </div>
      {contextHolder}
      <Table
        className="table margin"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={tableParams}
        rowKey="registration"
        scroll={{ x: 1660, y: scrollHeight }}
      />
    </>
  );
}

Onsite.propTypes = {
  site: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
};
